import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '@auth0/auth0-angular';
import { calculateWeeklyCost, Team, TeamMember, TeamMemberRatePeriod, TeamSprintSummary } from '../models/team.model';
import { AppUser, ProjectRole } from '../models/user.model';
import { BackendApiService } from './backend-api.service';
import { ProjectContextService } from './project-context.service';
import { TeamDto } from '../models/api-dto.model';

@Injectable({ providedIn: 'root' })
export class TeamService {
  private teams: Team[] = [];
  private teamsSubject = new BehaviorSubject<Team[]>([]);
  public teams$ = this.teamsSubject.asObservable();

  private currentTeamSubject = new BehaviorSubject<Team | null>(null);
  public currentTeam$ = this.currentTeamSubject.asObservable();

  private currentUserEmail: string = '';
  private currentUserDisplayName: string = '';
  private isLoaded = false;
  private loadInFlight: Observable<Team[]> | null = null;

  constructor(
    private backendApi: BackendApiService,
    private projectContext: ProjectContextService,
    private auth: AuthService
  ) {
    this.auth.user$.subscribe(user => {
      if (user) {
        this.currentUserEmail = user.email || '';
        const rawName = user.name || user.nickname || '';
        const isGeneric = !rawName || rawName.toLowerCase().includes('sprint') || rawName.toLowerCase() === 'user';
        this.currentUserDisplayName = isGeneric ? (this.currentUserEmail || 'Owner') : rawName;
        this.attachOwnerToActiveTeams();
      }
    });

    this.projectContext.currentProject$.subscribe(project => {
      if (project && project.id) {
        this.resetCache();
        this.loadTeams().subscribe();
      }
    });
  }

  getCurrentTeam(): Team {
    return this.currentTeamSubject.value || this.teams[0];
  }

  setCurrentTeam(team: Team): void {
    if (team) {
      this.currentTeamSubject.next(team);
    }
  }

  getTeams(): Observable<Team[]> {
    if (this.isLoaded && this.teams.length > 0) {
      return of(this.teams);
    }
    return this.loadTeams();
  }

  getTeamsSnapshot(): Team[] {
    return this.teams;
  }

  getTeam(reference: string): Observable<Team | undefined> {
    return this.getTeams().pipe(
      map(teams => teams.find(team => team.reference === reference || team.title === reference))
    );
  }

  loadTeams(): Observable<Team[]> {
    if (this.loadInFlight) {
      return this.loadInFlight;
    }

    this.loadInFlight = this.projectContext.ensureProjectId().pipe(
      switchMap(projectId => this.backendApi.getTeams(projectId).pipe(
        switchMap(dtos => {
          const activeDtos = (dtos || []).filter((d: any) => d.active !== false);
          if (activeDtos.length === 0) {
            // First-time owner: auto-create the initial Core Team
            return this.backendApi.createTeam(projectId, {
              title: 'Core Team',
              description: 'Default Development Team',
              defaultVelocity: 20
            }).pipe(
              map(createdDto => [createdDto])
            );
          }
          return of(activeDtos);
        }),
        map(dtos => {
          this.teams = dtos.map(dto => this.mapDtoToTeam(dto));
          this.attachOwnerToActiveTeams();
          this.isLoaded = true;
          this.teamsSubject.next(this.teams);

          const current = this.currentTeamSubject.value;
          if (!current || !this.teams.some(t => t.reference === current.reference)) {
            if (this.teams.length > 0) {
              this.currentTeamSubject.next(this.teams[0]);
            }
          }
          return this.teams;
        })
      )),
      tap({
        complete: () => {
          this.loadInFlight = null;
        }
      }),
      catchError(err => {
        this.loadInFlight = null;
        console.warn('TeamService: error loading teams from API', err);
        return of(this.teams);
      })
    );

    return this.loadInFlight;
  }

  private mapDtoToTeam(dto: TeamDto | any): Team {
    const title = dto.title || 'Core Team';
    const team = new Team(title);
    team.reference = dto.id || dto.teamId || team.reference;
    team.description = dto.description || '';
    team.active = dto.active !== false;
    team.activeSprintReference = dto.activeSprintId || undefined;
    team.defaultSprintRate = Number(dto.defaultVelocity) || 20;
    team.agentBudgetCents = Number(dto.agentBudgetCents ?? 0);
    team.agentBudgetSpentCents = Number(dto.agentBudgetSpentCents ?? 0);
    team.agentBudgetPrepaidCents = Number(dto.agentBudgetPrepaidCents ?? 0);

    if (Array.isArray(dto.members) && dto.members.length > 0) {
      team.members = dto.members.map((m: any) => {
        const member = new TeamMember(
          team.reference,
          m.email,
          m.displayName,
          m.role || 'Developer',
          m.projectRole || 'developer'
        );
        if (m.reference) member.reference = m.reference;
        member.weeklyHours = m.weeklyHours !== undefined ? Number(m.weeklyHours) : 38;
        member.rateAmount = m.rateAmount !== undefined ? Number(m.rateAmount) : 0;
        member.ratePeriod = m.ratePeriod || 'week';
        member.weeklyCost = m.weeklyCost !== undefined ? Number(m.weeklyCost) : calculateWeeklyCost(member);
        member.status = m.status || 'active';
        member.joinedAt = m.joinedAt;
        member.invitedAt = m.invitedAt;
        member.invitationUrl = m.invitationUrl;
        member.deliveryStatus = m.deliveryStatus;
        member.allocatedChapterReferences = m.allocatedChapterReferences || [];
        return member;
      });
    } else {
      team.members = [];
    }
    return team;
  }

  private attachOwnerToActiveTeams(): void {
    if (!this.currentUserEmail && !this.currentUserDisplayName) return;

    for (const team of this.teams) {
      const emailLower = (this.currentUserEmail || '').toLowerCase();
      const existing = team.members.find(m => m.email && m.email.toLowerCase() === emailLower);
      if (!existing) {
        // Add owner as active developer to the team so they can create tasks and sprints immediately
        const ownerMember = new TeamMember(
          team.reference,
          this.currentUserEmail || 'owner@sprinta.io',
          this.currentUserDisplayName || this.currentUserEmail || 'Owner',
          'Lead Developer',
          'developer'
        );
        ownerMember.status = 'active';
        ownerMember.joinedAt = new Date().toISOString();
        team.members.unshift(ownerMember);
      } else {
        // Owner exists! Preserve saved rateAmount, ratePeriod, weeklyHours, and weeklyCost.
        // Only update displayName if it is blank or a generic placeholder.
        const currentName = existing.displayName;
        const isGeneric = !currentName || currentName.toLowerCase().includes('sprint') || currentName.toLowerCase() === 'owner';
        if (isGeneric && this.currentUserDisplayName) {
          existing.displayName = this.currentUserDisplayName;
        }
      }
    }
  }

  createTeam(title = 'New Team'): Observable<Team> {
    return this.projectContext.ensureProjectId().pipe(
      switchMap(projectId => this.backendApi.createTeam(projectId, {
        title,
        description: 'Development Team',
        defaultVelocity: 20
      })),
      map(dto => {
        const team = this.mapDtoToTeam(dto);
        this.attachOwnerToActiveTeams();
        this.teams.push(team);
        this.teamsSubject.next(this.teams);
        return team;
      })
    );
  }

  saveTeam(team: Team): Observable<Team> {
    const membersPayload = (team.members || []).map(m => ({
      reference: m.reference,
      email: m.email,
      displayName: m.displayName,
      role: m.role || 'Developer',
      projectRole: m.projectRole || 'developer',
      weeklyHours: Number(m.weeklyHours) || 0,
      rateAmount: Number(m.rateAmount) || 0,
      ratePeriod: m.ratePeriod || 'week',
      weeklyCost: Number(m.weeklyCost) || 0,
      status: m.status || 'active',
      joinedAt: m.joinedAt || new Date().toISOString(),
      invitedAt: m.invitedAt,
      invitationUrl: m.invitationUrl,
      deliveryStatus: m.deliveryStatus,
      allocatedChapterReferences: m.allocatedChapterReferences || []
    }));

    return this.projectContext.ensureProjectId().pipe(
      switchMap(projectId => this.backendApi.updateTeam(projectId, team.reference, {
        title: team.title,
        description: team.description,
        defaultVelocity: team.defaultSprintRate,
        active: team.active,
        members: membersPayload,
        weeklyCostCents: Math.round(team.weeklyCost * 100),
        costPerPointCents: Math.round(team.costPerStoryPoint * 100),
        agentBudgetCents: team.agentBudgetCents,
        agentBudgetSpentCents: team.agentBudgetSpentCents,
        agentBudgetPrepaidCents: team.agentBudgetPrepaidCents
      })),
      map(dto => {
        const index = this.teams.findIndex(t => t.reference === team.reference);
        if (index >= 0) {
          const updated = this.mapDtoToTeam(dto);
          if (!updated.members || updated.members.length === 0) {
            updated.members = team.members;
          }
          this.teams[index] = updated;
          if (this.currentTeamSubject.value?.reference === team.reference) {
            this.currentTeamSubject.next(updated);
          }
          this.teamsSubject.next(this.teams);
        }
        return team;
      }),
      catchError(err => {
        console.warn('TeamService: updateTeam API error', err);
        return of(team);
      })
    );
  }

  topupAgentBudget(team: Team, amount: number, transactionId: string, referenceId?: string, cardLast4?: string, cardBrand?: string): Observable<Team> {
    const amountCents = Math.round(amount * 100);
    return this.projectContext.ensureProjectId().pipe(
      switchMap(projectId => this.backendApi.topupTeamAgentBudget(projectId, team.reference, {
        amount,
        amountCents,
        transactionId,
        referenceId,
        cardLast4,
        cardBrand
      })),
      map(res => {
        team.agentBudgetCents = Number(res.agentBudgetCents ?? (team.agentBudgetCents + amountCents));
        team.agentBudgetPrepaidCents = Number(res.agentBudgetPrepaidCents ?? (team.agentBudgetPrepaidCents + amountCents));
        this.teamsSubject.next(this.teams);
        if (this.currentTeamSubject.value?.reference === team.reference) {
          this.currentTeamSubject.next(team);
        }
        return team;
      }),
      catchError(err => {
        console.warn('TeamService: topupAgentBudget API error', err);
        return of(team);
      })
    );
  }

  deleteTeam(team: Team): Observable<boolean> {
    return this.projectContext.ensureProjectId().pipe(
      switchMap(projectId => this.backendApi.deleteTeam(projectId, team.reference)),
      map(() => {
        const index = this.teams.findIndex(t => t.reference === team.reference);
        if (index >= 0) {
          this.teams.splice(index, 1);
          if (this.teams.length > 0) {
            this.currentTeamSubject.next(this.teams[0]);
          }
          this.teamsSubject.next(this.teams);
          return true;
        }
        return false;
      }),
      catchError(err => {
        console.warn('TeamService: deleteTeam API error', err);
        return of(false);
      })
    );
  }

  public addTeamMember(teamReference: string, email: string, displayName = '', role = 'Developer', projectRole: ProjectRole = 'developer'): TeamMember | undefined {
    const team = this.teams.find(t => t.reference === teamReference);
    if (!team) return undefined;

    const normalizedEmail = email.toLowerCase().trim();
    const existing = team.members.find(m => m.email.toLowerCase().trim() === normalizedEmail);
    if (existing) {
      existing.status = 'active';
      this.teamsSubject.next(this.teams);
      return existing;
    }

    const newMember = new TeamMember(teamReference, email, displayName, role, projectRole);
    newMember.status = 'invited';
    newMember.deliveryStatus = 'sent';
    team.members.push(newMember);
    this.teamsSubject.next(this.teams);

    // Call live invitation endpoint in the background
    this.projectContext.ensureProjectId().pipe(
      switchMap(projectId => this.backendApi.createInvitation(projectId, {
        email,
        projectRole,
        teamId: teamReference
      }))
    ).subscribe({
      error: (err) => console.warn('TeamService: createInvitation API note', err)
    });

    return newMember;
  }

  findMemberTeam(email: string): Team | undefined {
    const normalized = email.toLowerCase().trim();
    return this.teams.find(team =>
      team.members.some(member => member.status === 'active' && member.email.toLowerCase() === normalized)
    );
  }

  getUserTeam(user: AppUser | null | undefined): Team | undefined {
    if (!user) return this.teams[0];
    const email = (user.email || '').toLowerCase().trim();
    const name = (user.displayName || '').toLowerCase().trim();
    const teamRefOrTitle = (user.teamReference || '').toLowerCase().trim();

    if (email) {
      const byEmail = this.teams.find(t =>
        t.members.some(m => m.status === 'active' && m.email && m.email.toLowerCase().trim() === email)
      );
      if (byEmail) return byEmail;
    }

    if (teamRefOrTitle) {
      const byRef = this.teams.find(t =>
        t.reference.toLowerCase() === teamRefOrTitle ||
        t.title.toLowerCase() === teamRefOrTitle
      );
      if (byRef) return byRef;
    }

    if (name) {
      const byName = this.teams.find(t =>
        t.members.some(m =>
          m.status === 'active' &&
          m.displayName &&
          (m.displayName.toLowerCase().trim() === name || name.includes(m.displayName.toLowerCase().trim()))
        )
      );
      if (byName) return byName;
    }

    return this.teams[0];
  }

  inviteMember(
    team: Team,
    email: string,
    displayName = '',
    role = 'Developer',
    rateAmount = 85,
    ratePeriod: TeamMemberRatePeriod = 'hour',
    weeklyHours = 38,
    projectRole: ProjectRole = 'developer',
    allocatedChapterReferences: string[] = []
  ): Observable<TeamMember> {
    const member = new TeamMember(team.reference, email.trim(), displayName.trim(), role);
    member.rateAmount = rateAmount;
    member.ratePeriod = ratePeriod;
    member.weeklyHours = weeklyHours;
    member.weeklyCost = calculateWeeklyCost(member);
    member.projectRole = projectRole;
    member.allocatedChapterReferences = [...allocatedChapterReferences];
    team.members.push(member);
    this.teamsSubject.next(this.teams);

    this.projectContext.ensureProjectId().pipe(
      switchMap(projectId => this.backendApi.createInvitation(projectId, {
        email: email.trim(),
        projectRole,
        teamId: team.reference,
        allocatedChapterIds: allocatedChapterReferences
      }))
    ).subscribe({
      error: (err) => console.warn('TeamService: createInvitation API note', err)
    });

    return of(member);
  }

  acceptInvitation(team: Team, member: TeamMember): Observable<TeamMember> {
    const otherTeam = this.findMemberTeam(member.email);
    if (otherTeam && otherTeam.reference !== team.reference) {
      const prevMember = otherTeam.members.find(m => m.email.toLowerCase() === member.email.toLowerCase());
      if (prevMember) {
        prevMember.status = 'removed';
      }
    }

    member.status = 'active';
    member.joinedAt = new Date().toISOString();
    member.weeklyCost = calculateWeeklyCost(member);
    this.teamsSubject.next(this.teams);
    return of(member);
  }

  removeMember(team: Team, member: TeamMember): Observable<void> {
    member.status = 'removed';
    this.teamsSubject.next(this.teams);
    return of(void 0);
  }

  addSprintSummary(teamReference: string, summary: TeamSprintSummary): Observable<Team | undefined> {
    const team = this.teams.find(t => t.reference === teamReference) || this.teams[0];
    if (team) {
      team.sprintHistory.unshift(summary);
      this.teamsSubject.next(this.teams);
    }
    return of(team);
  }

  resetCache(): void {
    this.teams = [];
    this.isLoaded = false;
    this.loadInFlight = null;
    this.currentTeamSubject.next(null);
    this.teamsSubject.next([]);
  }
}
